
import Vue, { ComponentOptions } from "vue";
import Component, { mixins } from "vue-class-component";
import {
  Emit,
  Inject,
  Model,
  Prop,
  Provide,
  Watch,
} from "vue-property-decorator";

import { CopyToClipboard } from '../clipboard-mixin';

/** SI megabytes, consistent with the humanize_bytes filter (1000-based). */
const LARGE_DOWNLOAD_WARNING_BYTES = 100 * 1000 * 1000;

export type DownloadLinkScope = "all" | "input" | "output";

export interface DownloadLinkRow {
  label: string;
  url: string;
  /** If set, confirmation copy names this download; otherwise generic. */
  downloadScope?: DownloadLinkScope | null;
  /** Approximate total size in bytes; omitted in dialog if unknown or non-positive. */
  approxBytes?: number | null;
}

@Component({
  filters: {},
})
export default class DownloadJobFilesTable extends mixins(CopyToClipboard) {
  @Prop({ type: String, default: "" })
  public url: string;

  @Prop({ type: Array })
  public links?: DownloadLinkRow[];

  public confirmDialogText: string = "";
  public pendingDownloadUrl: string | null = null;
  public pendingApproxBytes: number | null = null;

  filename(url: string): string | undefined {
    return new URL(url).pathname.split("/").pop();
  }

  get linkRows(): DownloadLinkRow[] {
    if (Array.isArray(this.links) && this.links.length > 0) {
      return this.links;
    }
    if (this.url) {
      const name = this.filename(this.url);
      return [{ label: name || "Download", url: this.url }];
    }
    return [];
  }

  get showLargeDownloadWarning(): boolean {
    const b = this.pendingApproxBytes;
    return b != null && b > LARGE_DOWNLOAD_WARNING_BYTES;
  }

  buildConfirmMessage(row: DownloadLinkRow): string {
    const scope = row.downloadScope;
    let subject: string;
    if (scope === "all") {
      subject = "all job files";
    } else if (scope === "input") {
      subject = "input files";
    } else if (scope === "output") {
      subject = "output files";
    } else {
      subject = "files";
    }
    const bytes = row.approxBytes;
    if (bytes != null && bytes > 0) {
      const fmt = (this.$options.filters as { humanize_bytes?: (b: number) => string })
        .humanize_bytes;
      const human = fmt ? fmt(bytes) : `${bytes} B`;
      return `Download ${subject} (${human} total)?`;
    }
    return `Download ${subject}?`;
  }

  requestBrowserDownload(row: DownloadLinkRow) {
    this.pendingDownloadUrl = row.url;
    const bytes = row.approxBytes;
    this.pendingApproxBytes =
      bytes != null && bytes > 0 ? bytes : null;
    this.confirmDialogText = this.buildConfirmMessage(row);
    (this.$refs.downloadConfirmDialog as MdDialog).open();
  }

  closeConfirmDialog() {
    (this.$refs.downloadConfirmDialog as MdDialog).close();
    this.pendingDownloadUrl = null;
    this.pendingApproxBytes = null;
  }

  confirmPendingDownload() {
    const url = this.pendingDownloadUrl;
    (this.$refs.downloadConfirmDialog as MdDialog).close();
    this.pendingDownloadUrl = null;
    this.pendingApproxBytes = null;
    if (url) {
      window.open(url, "_blank");
    }
  }
}
